/**
 * Who wrote a piece of feedback (SEL-822). Pages get `people` (everyone they name, by user id) from
 * the API; a name opens that person's profile card, whose email is a mailto: link. A writer the API
 * could not resolve (some records from before writer ids were stored) is shown by email.
 */
import type { ReactNode } from 'react'
import { ProfileHover } from './Avatar'
import type { People, Role } from '@/lib/types'

export function PersonName({ people, id, email, fallback = 'Unknown' }: {
  people?: People; id?: string | null; email?: string | null; fallback?: string
}) {
  const person = id ? people?.[id] : undefined
  if (!person) return <span className="person-name">{email || fallback}</span>
  return <ProfileHover user={person}><span className="person-name">{person.name || person.email}</span></ProfileHover>
}

/**
 * Where feedback came from, worded for the reader: the developer portal says "Developer", the
 * expert portal "Engineer" (as the stage stepper does). An expert is the task's own or a peer.
 */
export function feedbackSource(writerRole: string | null | undefined, viewer: Role, isTaskExpert = false): string {
  if (writerRole === 'engineer') return viewer === 'engineer' ? 'Developer' : 'Engineer'
  return isTaskExpert ? 'Task expert' : 'Peer expert'
}

/** "Peer expert · Maria Rossi", followed by anything the caller adds (a date). */
export function Byline({ source, people, id, email, children }: {
  source: string; people?: People; id?: string | null; email?: string | null; children?: ReactNode
}) {
  return <span className="byline">
    <span className="byline-source">{source}</span>
    <span aria-hidden="true">·</span>
    <PersonName people={people} id={id} email={email} />
    {children && <><span aria-hidden="true">·</span>{children}</>}
  </span>
}
